import {useMemo} from 'react'
import {useQuery} from '@tanstack/react-query'
import {usePageTitle} from '@/hooks/use-page-title'
import {useI18n} from '@/i18n'
import {useServices} from '@/services/context'
import {UnifiedSearchBar} from '@/components/shared/UnifiedSearchBar'
import {TableEmptyState} from '@/components/shared/TableEmptyState'
import {Badge, BadgeVariant} from '@/components/ui/badge'
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@/components/ui/table'
import {ClickableTableRow} from '@/components/shared/ClickableTableRow'
import {CONCEPT_ICON} from '@/lib/concepts'
import {routes} from '@/lib/routes'
import {Pagination} from '@/components/ui/pagination'
import {useServerPagination} from '@/hooks/use-server-pagination'
import {UserControllerUsersWithRolesEndpoint} from '@/types/endpoint-contracts'
import {MutedText} from '@/components/shared/MutedText'
import {InlineRow} from '@/components/shared/InlineRow'
import {PageLayout} from '@/components/shared/PageLayout'

/* eslint-disable i18next/no-literal-string -- type values are stable API codes */
const USER_TYPE_SERVICE_ACCOUNT = 'SERVICE_ACCOUNT'
/* eslint-enable i18next/no-literal-string */

export function RoleAssignments() {
    const i18n = useI18n()
    const services = useServices()
    usePageTitle(i18n.roleAssignments.pageTitle)

    const {
        page, pageSize, queryString,
        setPage, setPageSize, pageSizeOptions,
        searchAndFilters, setSearchAndFilters,
    } = useServerPagination({
        endpoint: UserControllerUsersWithRolesEndpoint,
        defaultSortBy: UserControllerUsersWithRolesEndpoint.sort.CREATED_AT,
    })

    const hasActiveFilters = useMemo(() => {
        return !!searchAndFilters.q
            || Object.keys(searchAndFilters.filters).length > 0
            || Object.keys(searchAndFilters.dynamicAttributes).length > 0
    }, [searchAndFilters])

    const {data: usersData, isLoading, error} = useQuery({
        queryKey: ['users-with-roles', queryString, page, pageSize],
        queryFn: () => services.users.list(Object.fromEntries(new URLSearchParams(queryString))),
    })

    const users = usersData?.content || []
    const totalItems = usersData?.totalElements || 0
    const totalPages = usersData?.totalPages || 0

    const detailRouteFor = (user: typeof users[number]) => {
        if (user.type === USER_TYPE_SERVICE_ACCOUNT) {
            return routes.admin.serviceAccounts.detail(user.id)
        }
        return null
    }

    return (
        <PageLayout
            variant="list"
            icon={CONCEPT_ICON.roleAssignment}
            loading={isLoading}
            error={error}
            title={i18n.roleAssignments.usersWithRoles}
            count={totalItems}
            description={`${i18n.roleAssignments.userCount({count: totalItems})}${hasActiveFilters ? ` ${i18n.common.matchingFilter}` : ''}. ${i18n.roleAssignments.oauthNote}`}
            data-testid="role-assignments-list"
            filters={
                <UnifiedSearchBar
                    contract={UserControllerUsersWithRolesEndpoint}
                    value={searchAndFilters}
                    onChange={setSearchAndFilters}
                    placeholder={i18n.roleAssignments.filterUsersPlaceholder}
                />
            }
            pagination={totalItems > 0 ? (
                <Pagination
                    page={page}
                    pageSize={pageSize}
                    totalItems={totalItems}
                    totalPages={totalPages}
                    onPageChange={setPage}
                    onPageSizeChange={setPageSize}
                    pageSizeOptions={pageSizeOptions}
                />
            ) : undefined}
        >
            <Table>
                <TableHeader>
                    <TableRow data-readonly>
                        <TableHead data-testid="col-name">{i18n.common.name}</TableHead>
                        <TableHead data-testid="col-type" data-help-key="type">{i18n.roleAssignments.type}</TableHead>
                        <TableHead data-testid="col-email">{i18n.roleAssignments.email}</TableHead>
                        <TableHead data-testid="col-status">{i18n.common.status}</TableHead>
                        <TableHead data-testid="col-assigned-roles">{i18n.roleAssignments.assignedRoles}</TableHead>
                    </TableRow>
                </TableHeader>
                <TableBody>
                    {users.map((user) => {
                        const target = detailRouteFor(user)
                        const isServiceAccount = user.type === USER_TYPE_SERVICE_ACCOUNT
                        const typeBadgeVariant = isServiceAccount ? BadgeVariant.SECONDARY : BadgeVariant.OUTLINE
                        const typeLabel = isServiceAccount
                            ? i18n.roleAssignments.typeServiceAccount
                            : i18n.roleAssignments.typeUser
                        const cells = (
                            <>
                                <TableCell className="font-medium" data-testid={`user-name-${user.id}`}>
                                    {user.name}
                                </TableCell>
                                <TableCell>
                                    <Badge variant={typeBadgeVariant} data-testid={`user-type-badge-${user.id}`}>
                                        {typeLabel}
                                    </Badge>
                                </TableCell>
                                <TableCell data-testid={`user-email-${user.id}`}>
                                    <MutedText>{user.email ?? '—'}</MutedText>
                                </TableCell>
                                <TableCell>
                                    <Badge
                                        variant={user.active ? BadgeVariant.SUCCESS : BadgeVariant.SECONDARY}
                                        data-testid={`user-status-${user.id}`}
                                    >
                                        {user.active ? i18n.common.active : i18n.common.inactive}
                                    </Badge>
                                </TableCell>
                                <TableCell data-testid={`user-roles-${user.id}`}>
                                    <InlineRow gap="xs" wrap>
                                        {user.roles.map((role) => (
                                            <Badge
                                                key={role.id}
                                                variant="outline"
                                                className="text-xs"
                                                data-testid={`user-role-badge-${user.id}-${role.id}`}
                                            >
                                                {role.name}
                                            </Badge>
                                        ))}
                                        {user.roles.length === 0 && (
                                            <MutedText size="sm">{i18n.common.noRoles}</MutedText>
                                        )}
                                    </InlineRow>
                                </TableCell>
                            </>
                        )
                        if (target) {
                            return (
                                <ClickableTableRow
                                    key={user.id}
                                    to={target}
                                    data-testid={`user-row-${user.id}`}
                                >
                                    {cells}
                                </ClickableTableRow>
                            )
                        }
                        return (
                            <TableRow key={user.id} data-testid={`user-row-${user.id}`} data-readonly>
                                {cells}
                            </TableRow>
                        )
                    })}
                    {users.length === 0 && (
                        <TableEmptyState
                            colSpan={5}
                            hasFilters={hasActiveFilters}
                            matchMessage={i18n.roleAssignments.noUsersMatch}
                            emptyMessage={i18n.roleAssignments.noUsersFound}
                        />
                    )}
                </TableBody>
            </Table>
        </PageLayout>
    )
}
